import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";

// Strip ONLY a bare trailing `#` (empty hash) from the URL.
// The Lovable OAuth broker leaves `/admin#` after Google sign-in once it
// has consumed the tokens. We must NEVER touch a hash that still has
// content — Supabase's `detectSessionInUrl` and the OAuth broker may use
// hash params we don't recognize, and stripping them breaks the session.
function stripEmptyHashOnly(): void {
  try {
    const href = window.location.href;
    const hash = window.location.hash || '';
    // Only act when the hash is literally empty (just `#` at the end).
    if (hash === '' && href.endsWith('#')) {
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search,
      );
    }
  } catch {
    /* ignore */
  }
}

stripEmptyHashOnly();
// Watch for the moment the OAuth broker / Supabase finish consuming the
// hash and leave a bare `#` behind. `hashchange` fires when they call
// `history.replaceState` with an empty hash on most browsers; we also
// poll a few times as a safety net.
window.addEventListener('hashchange', stripEmptyHashOnly);
window.addEventListener('popstate', stripEmptyHashOnly);
[100, 300, 600, 1200, 2500].forEach((ms) =>
  window.setTimeout(stripEmptyHashOnly, ms),
);

// Auto-recover from stale lazy-chunk errors after a deploy. When the user's
// already-loaded bundle references chunk filenames that no longer exist on
// the server, the dynamic import rejects and React renders nothing. We do
// a one-time hard reload (guarded via sessionStorage) so the browser fetches
// the new index.html with up-to-date chunk hashes.
const RELOAD_KEY = 'chunk-reload-attempted';
function isChunkLoadError(message: string): boolean {
  return /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(
    message,
  );
}
function maybeReloadOnce(message: string): void {
  if (!isChunkLoadError(message)) return;
  try {
    if (sessionStorage.getItem(RELOAD_KEY)) return;
    sessionStorage.setItem(RELOAD_KEY, '1');
    window.location.reload();
  } catch {
    /* ignore */
  }
}
window.addEventListener('error', (e) => {
  maybeReloadOnce(e?.message ?? '');
});
window.addEventListener('unhandledrejection', (e) => {
  const reason: any = e?.reason;
  const msg = typeof reason === 'string' ? reason : reason?.message ?? '';
  maybeReloadOnce(msg);
});

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>,
);

